import { format, startOfDay } from "date-fns";
import React, { useEffect, useRef, useState } from "react";

import {
	GetUserCalendarIntegrationStatusResponse,
	MeetingModelJson,
	UpdateMeetingRequestJson,
} from "@performyard/service-contracts";

import { CalendarErrorModal } from "./CalendarErrorModal";

import { useEditWorkspaceMeeting } from "../../../../../../api/hooks";
import {
	useCompanyCalendarIntegrationSettings,
	useUserCalendarIntegrationStatus,
} from "../../../../../../api/hooks/defs/calendar-integration";
import { useFeatureFlagQuery } from "../../../../../../hooks/useFeatureFlag";
import useForm from "../../../../../../hooks/useForm";
import { useCalendarErrorHandler, useValidationErrorClearer } from "../../../hooks";

import { getDefaultMeetingTimes } from "../../../utils";

import { MeetingFormSchema, formSchema } from "../../../schema";
import MeetingForm, { MeetingFormData } from "../MeetingForm";

// Keep default values outside component to prevent recreation on each render
const getDefaultFormValues = (): MeetingFormSchema => {
	const defaultTimes = getDefaultMeetingTimes();
	return {
		name: "",
		startDate: startOfDay(defaultTimes.startDateTime),
		endDate: startOfDay(defaultTimes.endDateTime),
		startTime: format(defaultTimes.startDateTime, "HH:mm"),
		endTime: format(defaultTimes.endDateTime, "HH:mm"),
		description: "",
		carryOver: false,
		addToCalendar: false,
	};
};

/**
 * Checks if a meeting's calendar event will be automatically updated based on the user's current calendar integration
 * @param meeting - The meeting to check
 * @param userCalendarStatus - The user's current calendar integration status
 * @returns true if the calendar event will be updated automatically
 */
const willCalendarEventBeUpdated = (
	meeting: MeetingModelJson | null,
	userCalendarStatus: GetUserCalendarIntegrationStatusResponse | undefined
): boolean => {
	if (!meeting?.calendarEvent || !userCalendarStatus) {
		return false;
	}

	// Check if meeting has a calendar event
	const hasValidCalendarEvent =
		meeting.calendarEvent.eventId && meeting.calendarEvent.provider && meeting.calendarEvent.calendarId;

	if (!hasValidCalendarEvent) {
		return false;
	}

	// Check if user's current calendar integration matches the meeting's calendar event
	const isCalendarIntegrationMatched =
		userCalendarStatus.isConnected &&
		userCalendarStatus.provider === meeting.calendarEvent.provider &&
		userCalendarStatus.selectedCalendarId === meeting.calendarEvent.calendarId;

	return isCalendarIntegrationMatched;
};

interface EditMeetingModalProps {
	isOpen: boolean;
	handleClose: () => void;
	meeting: MeetingModelJson | null;
}

export const EditMeetingModal = ({ isOpen, handleClose, meeting }: EditMeetingModalProps) => {
	const [validationErrors, setValidationErrors] = useState<Partial<Record<keyof MeetingFormSchema, string>>>({});

	// Capture calendar notification when modal is open to persist during closing animation
	const calendarNotificationRef = useRef<string | undefined>(undefined);

	const { value: ffIsCalendarIntegrationEnabled } = useFeatureFlagQuery("ff-is-calendar-integration-enabled", false);
	const { data: companyCalendarIntegrationSettings } = useCompanyCalendarIntegrationSettings({
		enabled: ffIsCalendarIntegrationEnabled,
	});
	const { data: userCalendarStatus } = useUserCalendarIntegrationStatus({
		enabled: ffIsCalendarIntegrationEnabled && companyCalendarIntegrationSettings?.enabled === true,
	});
	const { modalState: calendarModalState, handleCalendarError, closeModal, setRetrying } = useCalendarErrorHandler();

	const { mutate: editWorkspaceMeeting, isPending } = useEditWorkspaceMeeting(handleClose, (error, variables) => {
		// Create calendar error context for the centralized handler
		const context = {
			operation: "update" as const,
			meetingData: variables.data,
			meetingId: variables.meetingId,
		};
		return handleCalendarError(error, context);
	});

	const {
		validate,
		updateForm,
		state: formValues,
	} = useForm({
		schema: formSchema,
		defaultValues: getDefaultFormValues(),
		disableWarning: true,
	});

	// Update form when modal opens
	useEffect(() => {
		if (isOpen && meeting) {
			const startDateTime = new Date(meeting.startDateTime);
			const endDateTime = new Date(meeting.endDateTime);

			updateForm({
				name: meeting.name,
				startDate: startOfDay(startDateTime),
				endDate: startOfDay(endDateTime),
				startTime: format(startDateTime, "HH:mm"),
				endTime: format(endDateTime, "HH:mm"),
				description: meeting.description || "",
				carryOver: false,
				addToCalendar: false, // Always false for edit modal - calendar updates are handled automatically server-side
			});
			setValidationErrors({});

			// Capture calendar notification for closing animation
			const willUpdate = willCalendarEventBeUpdated(meeting, userCalendarStatus);
			calendarNotificationRef.current = willUpdate
				? "Any updates to this meeting will automatically update in your connected calendar."
				: undefined;
		}
	}, [isOpen, meeting, userCalendarStatus]);

	// Use current notification if modal is open, otherwise use captured notification for closing animation
	const showCalendarUpdateNotification = willCalendarEventBeUpdated(meeting, userCalendarStatus);
	const calendarNotification = isOpen
		? showCalendarUpdateNotification
			? "Any updates to this meeting will automatically update in your connected calendar."
			: undefined
		: calendarNotificationRef.current;

	// Custom hook for clearing validation errors when form values change
	useValidationErrorClearer(formValues, validationErrors, setValidationErrors);

	const handleSubmit = (data: MeetingFormData) => {
		if (!meeting) return;

		// Validate form
		const validationResult = validate();
		if (!validationResult.isValid) {
			const errors = Object.entries(validationResult.errors || {}).reduce(
				(acc, [key, error]) => {
					acc[key as keyof MeetingFormSchema] = error.message;
					return acc;
				},
				{} as Partial<Record<keyof MeetingFormSchema, string>>
			);
			setValidationErrors(errors);
			return;
		}

		const formattedData: UpdateMeetingRequestJson = {
			...data,
			startDateTime: data.startDateTime.toISOString(),
			endDateTime: data.endDateTime.toISOString(),
		};

		editWorkspaceMeeting({ meetingId: meeting._id, data: formattedData });
	};

	// Reset form and close modal
	const onClose = () => {
		// If calendar error modal is open, just close it without calling handleClose
		if (calendarModalState.type !== "none") {
			closeModal();
			return;
		}

		setValidationErrors({});
		handleClose();
	};

	return (
		<>
			{/* Only show MeetingForm when calendar error modal is not open */}
			{calendarModalState.type === "none" && (
				<MeetingForm
					isOpen={isOpen}
					onClose={onClose}
					onSubmit={handleSubmit}
					formValues={formValues}
					onFormChange={updateForm}
					validationErrors={validationErrors}
					submitLabel="Save Changes"
					isPending={isPending}
					title="Edit Meeting Details"
					showCalendarIntegration={false}
					calendarUpdateNotification={calendarNotification}
				/>
			)}

			<CalendarErrorModal
				modalState={calendarModalState}
				onRetry={() => {
					if (calendarModalState.type === "error") {
						setRetrying(true);
						const { context } = calendarModalState;
						if (context.operation === "update") {
							editWorkspaceMeeting(
								{ meetingId: context.meetingId, data: context.meetingData },
								{
									onSuccess: () => closeModal(),
									onError: () => setRetrying(false),
								}
							);
						}
					}
				}}
				onContinue={() => {
					if (calendarModalState.type === "error") {
						// For update operations, just close the modal since we can't do partial updates
						closeModal();
					}
				}}
				onClose={closeModal}
			/>
		</>
	);
};
